import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelCheckbox as _vModelCheckbox, createCommentVNode as _createCommentVNode, unref as _unref, vModelText as _vModelText } from "vue"

const _hoisted_1 = { class: "date-range" }
const _hoisted_2 = { class: "font-sm" }
const _hoisted_3 = { class: "date-range" }
const _hoisted_4 = {
  key: 0,
  class: "search-type"
}
const _hoisted_5 = { class: "font-sm" }
const _hoisted_6 = ["id", "value"]
const _hoisted_7 = ["for"]
const _hoisted_8 = { class: "road-name" }
const _hoisted_9 = { class: "font-sm" }
const _hoisted_10 = ["onClick"]
const _hoisted_11 = ["id", "value", "onUpdate:modelValue"]
const _hoisted_12 = ["for"]
const _hoisted_13 = ["id", "value"]
const _hoisted_14 = ["for"]
const _hoisted_15 = { class: "direction" }
const _hoisted_16 = { class: "font-sm" }
const _hoisted_17 = ["id", "value"]
const _hoisted_18 = ["for"]
const _hoisted_19 = {
  key: 1,
  class: "kp"
}
const _hoisted_20 = { class: "font-sm" }
const _hoisted_21 = { class: "form-group mb0 mr16 ml15" }
const _hoisted_22 = { class: "ws-nowrap" }
const _hoisted_23 = ["disabled"]
const _hoisted_24 = ["disabled"]

import {
  reactive,
  watch,
  watchEffect,
  computed,
  onMounted,
} from 'vue';
import type { RoadName, RoadNameDisp } from 'src/models/master';
import type { CommonSearchParams, SearchType, SearchTypeMap } from 'src/models/geoItemSearch';
import { useReactiveMasters } from 'src/composables/useMasters';
import { dtFormat } from 'src/lib/dateHelper';

interface SearchParams {
  searchTypes: Array<string | number>;
  tsFrom: Date | null;
  tsTo: Date | null;
  roads: RoadName[];
  directions: string[];
  // キロポストは入力欄の生の値。親へ渡すときに数値化する
  kpCalcFrom: string;
  kpCalcTo: string;
}

interface IndexRoadName {
  idx: number;
  elems: RoadNameDisp[];
}

type RoadNamesByAreaMap = Record<string, IndexRoadName>;

interface GeoItemSearchCommentState {
  showSearchTypes: boolean;
  showDateRange: boolean;
  showRoads: boolean;
  showRoadByAreas: Record<string, boolean>;
  showDirections: boolean;
  showKpRange: boolean;
  search: SearchParams;
}

// withDefaultsでdefaultを与えても、TS上optional(?)でないとrequired:trueのままになるVueの仕様
// https://github.com/vuejs/core/blob/v3.5.35/packages/compiler-sfc/src/script/resolveType.ts (required: !e.optional)
// defaultを持つpropsはoptionalにしている(checkboxKeyPrefixは全呼び出し元が渡しているため必須のまま)
interface Props {
  checkboxKeyPrefix: string | null;
  searchTypeMap?: SearchTypeMap;
  searchTypes?: Array<SearchType>;
  searchTypeDefaultValues?: Array<string | number>;
  isKpSearch?: boolean;
  defaultDaysBefore?: number;
  defaultMonthsBefore?: number;
  maxSearchRangeMonths?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GeoItemSearchCommon',
  props: /*@__PURE__*/_mergeModels({
    checkboxKeyPrefix: {},
    searchTypeMap: { default: () => ({}) },
    searchTypes: { default: () => [] },
    searchTypeDefaultValues: { default: () => [] },
    isKpSearch: { type: Boolean, default: true },
    defaultDaysBefore: { default: 0 },
    defaultMonthsBefore: { default: 3 },
    maxSearchRangeMonths: { default: 3 }
  }, {
    "searchParams": { default: null },
    "searchParamsModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(['change'], ["update:searchParams"]),
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const initSearchState = (): SearchParams => {
  return {
    searchTypes: [],
    tsFrom: null,
    tsTo: null,
    roads: [],
    directions: [],
    kpCalcFrom: '',
    kpCalcTo: '',
  };
};
const state = reactive<GeoItemSearchCommentState>({
  showSearchTypes: false,
  showDateRange: false,
  showRoads: false,
  showRoadByAreas: {},
  showDirections: false,
  showKpRange: false,
  search: initSearchState(),
});

const getRoadNamesByAreas = (): [string, IndexRoadName][] => {
  let areaIdx = 0;
  const roadNamesByAreaMap = Object.values(masters.kp.roadNameDispMap).reduce((acc: RoadNamesByAreaMap, e) => {
    if (!acc[e.area]) {
      acc[e.area] = { idx: areaIdx++, elems: [] };
    }
    acc[e.area].elems.push(e);
    return acc;
  }, {});
  const areas = Object.keys(roadNamesByAreaMap);
  if (areas.length < 2) {
    return [];
  }
  return Object.entries(roadNamesByAreaMap).sort((a, b) => a[1].idx < b[1].idx ? -1 : 1);
};

const roadNamesByAreas = computed<[string, IndexRoadName][]>(getRoadNamesByAreas);

const setInitialSearchParams = () => {
  const now = new Date();
  const tsFrom = props.defaultDaysBefore > 0
    ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - props.defaultDaysBefore)
    : new Date(now.getFullYear(), now.getMonth() - props.defaultMonthsBefore, now.getDate());
  const params = {
    searchTypes: props.searchTypeDefaultValues.length > 0
      ? props.searchTypeDefaultValues
      : props.searchTypes.map(e => e.key),
    tsFrom: tsFrom,
    tsTo: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
    roads: [],
    directions: [],
    kpCalcFrom: '',
    kpCalcTo: '',
  };
  state.search = params;
};

const { masters, resolveMastersPromise } = useReactiveMasters();
onMounted(async() => {
  await resolveMastersPromise();
  state.showRoadByAreas = roadNamesByAreas.value.reduce((acc: Record<string, boolean>, curr) => {
    acc[curr[0]] = false;
    return acc;
  }, {});
  setInitialSearchParams();
});
watch(() => props.searchTypes, () => {
  state.search.searchTypes = props.searchTypes.map(e => e.key);
});

// computed
const selectedSearchTypesDisp = computed(() => {
  if (state.search.searchTypes.length === 0) return '';

  const ret = state.search.searchTypes.slice().sort((a, b) => {
    const val1 = props.searchTypeMap[a].dispOrder;
    const val2 = props.searchTypeMap[b].dispOrder;
    return val1 < val2 ? -1 : val1 > val2 ? 1 : 0;
  }).map(e => props.searchTypeMap[e].val).join(', ');
  return `（${ret}）`;
});
const selectedRoadsDisp = computed(() => {
  if (state.search.roads.length === 0) return '';

  const ret = state.search.roads.map(e => e.roadNameDispShort).join(', ');
  return `（${ret}）`;
});
const selectedDirectionsDisp = computed(() => {
  if (state.search.directions.length === 0) return '';

  const ret = state.search.directions.join(', ');
  return `（${ret}）`;
});
const kpRangeDisp = computed(() => {
  if (!state.search.kpCalcFrom && !state.search.kpCalcTo) return '';

  return `（${state.search.kpCalcFrom}〜${state.search.kpCalcTo}）`;
});
const directionOptions = computed(() =>
  [...new Set(state.search.roads.flatMap(e => e.directions))],
);
const dateRangeDisp = computed(() => {
  const tsFrom = dtFormat(state.search.tsFrom, 'yyyy/mm/dd');
  const tsTo = dtFormat(state.search.tsTo, 'yyyy/mm/dd');
  const ret = tsFrom + '〜' + tsTo;
  return `（${ret}）`;
});
const disabledKpFields = computed(() => {
  return state.search.roads.length === 0 || state.search.directions.length === 0;
});

// methods
// 親（GeoItemSearch*）へは v-model:search-params で派生パラメータを反映する（命令的な ref 呼び出しを廃止）
const searchParamsModel = _useModel<CommonSearchParams | null>(__props, 'searchParams');
// 未入力・数値でない入力は「範囲指定なし」として null にする。
// 空文字のまま送ると Laravel 側で null になり、検索条件の値として使えない
const toKpCalc = (val: string): number | null => {
  const num = parseFloat(val);
  return isNaN(num) ? null : num;
};
// flush: 'sync' で state.search 変更を同期的に model へ反映する。
// デフォルトの 'pre' はマイクロタスクに遅延するため、同期的な @change → getSearchParams()
// のチェーンが 1 つ前の検索条件を読んでしまう不具合を防ぐ。
watchEffect(() => {
  searchParamsModel.value = {
    searchTypes: state.search.searchTypes,
    tsFrom: state.search.tsFrom,
    tsTo: state.search.tsTo ? new Date(state.search.tsTo.valueOf() + 86400 * 1000) : state.search.tsTo,
    roadNameDisps: state.search.roads.map(e => e.roadNameDisp),
    directions: state.search.directions,
    kpCalcFrom: toKpCalc(state.search.kpCalcFrom),
    kpCalcTo: toKpCalc(state.search.kpCalcTo),
  };
}, { flush: 'sync' });
const onSearchTypeClick = (e: MouseEvent) => {
  if (state.search.searchTypes.length > 1) return;
  if ((e.target as HTMLInputElement).checked) return;

  // 種別は最低一つ選択必須
  e.preventDefault();
  e.stopPropagation();
};
const adjustSearchDateRange = (prop: string) => {
  const [propVal, otherPropVal, setToOtherProp, sign] =
    prop === 'from'
      ? [state.search.tsFrom, state.search.tsTo, (arg: Date) => { state.search.tsTo = arg; }, 1]
      : [state.search.tsTo, state.search.tsFrom, (arg: Date) => { state.search.tsFrom = arg; }, -1];
  if (!propVal) return;

  const borderRangeDate = new Date(
    propVal.getFullYear(),
    propVal.getMonth() + sign * props.maxSearchRangeMonths,
    propVal.getDate(),
  );

  // 編集していない方の日付が正しい日付ではない場合、編集していない方の日付を調整する
  if (!otherPropVal) {
    setToOtherProp(borderRangeDate);
    return;
  }

  const params = state.search;
  // 開始日 > 終了日の場合、編集していない方の日付を調整する
  if (params.tsFrom && params.tsTo && params.tsFrom > params.tsTo) {
    setToOtherProp(borderRangeDate);
    return;
  }

  // 開始日 <= 終了日 の場合、borderRangeDateの範囲を超えないよう、編集していない方の日付を調整する
  if (prop === 'from') {
    setToOtherProp(new Date(Math.min(otherPropVal.getTime(), borderRangeDate.getTime())));
  } else {
    setToOtherProp(new Date(Math.max(otherPropVal.getTime(), borderRangeDate.getTime())));
  }
};
const onChange = () => {
  emit('change');
};
const onDateParamChange = (prop: string, val: Date) => {
  if (prop === 'from') {
    state.search.tsFrom = val;
  } else if (prop === 'to') {
    state.search.tsTo = val;
  }

  adjustSearchDateRange(prop);

  if (state.search.tsFrom && state.search.tsTo) {
    onChange();
  }
};
const onRoadsOrDirectionsChanged = () => {
  if (disabledKpFields.value) {
    state.search.kpCalcFrom = '';
    state.search.kpCalcTo = '';
  }
  onChange();
};

return (_ctx: any,_cache: any) => {
  const _component_MyCalendar = _resolveComponent("MyCalendar")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("span", {
        class: "title-area",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (state.showDateRange = !state.showDateRange))
      }, [
        _createElementVNode("i", {
          class: _normalizeClass(["fas mr4", {
            'fa-caret-right': !state.showDateRange,
            'fa-caret-down': state.showDateRange,
          }])
        }, null, 2),
        _cache[13] || (_cache[13] = _createTextVNode(" 期間", -1)),
        _createElementVNode("span", _hoisted_2, _toDisplayString(dateRangeDisp.value), 1)
      ]),
      _withDirectives(_createElementVNode("div", _hoisted_3, [
        _createVNode(_component_MyCalendar, {
          class: "date-from",
          "date-value": state.search.tsFrom,
          "set-date-value-once": false,
          onDateInput: _cache[1] || (_cache[1] = ($event: any) => (onDateParamChange('from', $event)))
        }, null, 8, ["date-value"]),
        _cache[14] || (_cache[14] = _createElementVNode("span", { class: "ib waveline ta-center" }, "〜", -1)),
        _createVNode(_component_MyCalendar, {
          class: "date-to",
          "date-value": state.search.tsTo,
          "set-date-value-once": false,
          onDateInput: _cache[2] || (_cache[2] = ($event: any) => (onDateParamChange('to', $event)))
        }, null, 8, ["date-value"])
      ], 512), [
        [_vShow, state.showDateRange]
      ])
    ]),
    (__props.searchTypes.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("span", {
            class: "title-area",
            onClick: _cache[3] || (_cache[3] = ($event: any) => (state.showSearchTypes = !state.showSearchTypes))
          }, [
            _createElementVNode("i", {
              class: _normalizeClass(["fas mr4", {
            'fa-caret-right': !state.showSearchTypes,
            'fa-caret-down': state.showSearchTypes,
          }])
            }, null, 2),
            _cache[15] || (_cache[15] = _createTextVNode(" 種別", -1)),
            _createElementVNode("span", _hoisted_5, _toDisplayString(selectedSearchTypesDisp.value), 1)
          ]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.searchTypes, (e) => {
            return _withDirectives((_openBlock(), _createElementBlock("div", {
              class: "custom-control custom-checkbox ml15",
              key: e.key
            }, [
              _withDirectives(_createElementVNode("input", {
                type: "checkbox",
                class: "custom-control-input",
                id: `${__props.checkboxKeyPrefix}-search-type-${e.key}`,
                value: e.key,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((state.search.searchTypes) = $event)),
                onClick: _cache[5] || (_cache[5] = ($event: any) => (onSearchTypeClick($event))),
                onChange: onChange
              }, null, 40, _hoisted_6), [
                [_vModelCheckbox, state.search.searchTypes]
              ]),
              _createElementVNode("label", {
                class: "custom-control-label",
                for: `${__props.checkboxKeyPrefix}-search-type-${e.key}`
              }, _toDisplayString(e.val), 9, _hoisted_7)
            ])), [
              [_vShow, state.showSearchTypes]
            ])
          }), 128))
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_8, [
      _createElementVNode("span", {
        class: "title-area",
        onClick: _cache[6] || (_cache[6] = ($event: any) => (state.showRoads = !state.showRoads))
      }, [
        _createElementVNode("i", {
          class: _normalizeClass(["fas mr4", {
            'fa-caret-right': !state.showRoads,
            'fa-caret-down': state.showRoads,
          }])
        }, null, 2),
        _cache[16] || (_cache[16] = _createTextVNode(" 路線", -1)),
        _createElementVNode("span", _hoisted_9, _toDisplayString(selectedRoadsDisp.value), 1)
      ]),
      (roadNamesByAreas.value.length > 0)
        ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(roadNamesByAreas.value, ([area, roads]) => {
            return _withDirectives((_openBlock(), _createElementBlock("div", {
              key: area,
              class: "ml12"
            }, [
              _createElementVNode("span", {
                class: "title-area",
                onClick: ($event: any) => (state.showRoadByAreas[area] = !state.showRoadByAreas[area])
              }, [
                _createElementVNode("i", {
                  class: _normalizeClass(["fas mr4", {
                'fa-caret-right': !state.showRoadByAreas[area],
                'fa-caret-down': state.showRoadByAreas[area],
              }])
                }, null, 2),
                _createTextVNode(" " + _toDisplayString(area), 1)
              ], 8, _hoisted_10),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(roads.elems, (road, roadIdx) => {
                return _withDirectives((_openBlock(), _createElementBlock("div", {
                  class: "custom-control custom-checkbox ml15",
                  key: roadIdx
                }, [
                  _withDirectives(_createElementVNode("input", {
                    type: "checkbox",
                    class: "custom-control-input",
                    id: `${__props.checkboxKeyPrefix}-${area}-${roadIdx}`,
                    value: road,
                    "onUpdate:modelValue": ($event: any) => ((state.search.roads) = $event),
                    onChange: onRoadsOrDirectionsChanged
                  }, null, 40, _hoisted_11), [
                    [_vModelCheckbox, state.search.roads]
                  ]),
                  _createElementVNode("label", {
                    class: "custom-control-label",
                    for: `${__props.checkboxKeyPrefix}-${area}-${roadIdx}`
                  }, _toDisplayString(road.roadNameDispShort), 9, _hoisted_12)
                ])), [
                  [_vShow, state.showRoadByAreas[area]]
                ])
              }), 128))
            ])), [
              [_vShow, state.showRoads]
            ])
          }), 128))
        : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(_unref(masters).kp.roadNameDispMap, (e, idx) => {
            return _withDirectives((_openBlock(), _createElementBlock("div", {
              class: "custom-control custom-checkbox ml15",
              key: idx
            }, [
              _withDirectives(_createElementVNode("input", {
                type: "checkbox",
                class: "custom-control-input",
                id: `${__props.checkboxKeyPrefix}-roads-${idx}`,
                value: e,
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((state.search.roads) = $event)),
                onChange: onRoadsOrDirectionsChanged
              }, null, 40, _hoisted_13), [
                [_vModelCheckbox, state.search.roads]
              ]),
              _createElementVNode("label", {
                class: "custom-control-label",
                for: `${__props.checkboxKeyPrefix}-roads-${idx}`
              }, _toDisplayString(e.roadNameDispShort), 9, _hoisted_14)
            ])), [
              [_vShow, state.showRoads]
            ])
          }), 128))
    ]),
    _createElementVNode("div", _hoisted_15, [
      _createElementVNode("span", {
        class: "title-area",
        onClick: _cache[8] || (_cache[8] = ($event: any) => (state.showDirections = !state.showDirections))
      }, [
        _createElementVNode("i", {
          class: _normalizeClass(["fas mr4", {
            'fa-caret-right': !state.showDirections,
            'fa-caret-down': state.showDirections,
          }])
        }, null, 2),
        _cache[17] || (_cache[17] = _createTextVNode(" 方向", -1)),
        _createElementVNode("span", _hoisted_16, _toDisplayString(selectedDirectionsDisp.value), 1)
      ]),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(directionOptions.value, (e, idx) => {
        return _withDirectives((_openBlock(), _createElementBlock("div", {
          class: "custom-control custom-checkbox ml15",
          key: idx
        }, [
          _withDirectives(_createElementVNode("input", {
            type: "checkbox",
            class: "custom-control-input",
            id: `${__props.checkboxKeyPrefix}-directions-${idx}`,
            value: e,
            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((state.search.directions) = $event)),
            onChange: onRoadsOrDirectionsChanged
          }, null, 40, _hoisted_17), [
            [_vModelCheckbox, state.search.directions]
          ]),
          _createElementVNode("label", {
            class: "custom-control-label",
            for: `${__props.checkboxKeyPrefix}-directions-${idx}`
          }, _toDisplayString(e), 9, _hoisted_18)
        ])), [
          [_vShow, state.showDirections]
        ])
      }), 128))
    ]),
    (__props.isKpSearch)
      ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
          _createElementVNode("span", {
            class: "title-area",
            onClick: _cache[10] || (_cache[10] = ($event: any) => (state.showKpRange = !state.showKpRange))
          }, [
            _createElementVNode("i", {
              class: _normalizeClass(["fas mr4", {
            'fa-caret-right': !state.showKpRange,
            'fa-caret-down': state.showKpRange,
          }])
            }, null, 2),
            _cache[18] || (_cache[18] = _createTextVNode(" キロポスト", -1)),
            _createElementVNode("span", _hoisted_20, _toDisplayString(kpRangeDisp.value), 1)
          ]),
          _withDirectives(_createElementVNode("div", _hoisted_21, [
            _createElementVNode("div", _hoisted_22, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((state.search.kpCalcFrom) = $event)),
                type: "text",
                class: "ib form-control kp-calc-from",
                disabled: disabledKpFields.value,
                onChange: onChange
              }, null, 40, _hoisted_23), [
                [_vModelText, state.search.kpCalcFrom]
              ]),
              _cache[19] || (_cache[19] = _createElementVNode("span", { class: "ib waveline ta-center" }, "〜", -1)),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((state.search.kpCalcTo) = $event)),
                type: "text",
                class: "ib form-control kp-calc-to",
                disabled: disabledKpFields.value,
                onChange: onChange
              }, null, 40, _hoisted_24), [
                [_vModelText, state.search.kpCalcTo]
              ])
            ])
          ], 512), [
            [_vShow, state.showKpRange]
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})